import { useState, useRef } from "react";
import { Check, ShieldCheck, Clock, Award, BadgeDollarSign, Phone } from "lucide-react";
import SEO, { localBusinessSchema } from "../components/SEO";
import { Logo } from "../components/Header";
import { SectionLabel, Stars } from "../components/Shared";
import { LeadForm } from "../components/LeadForm";
import InstantEstimator from "../components/InstantEstimator";
import { BRAND, IMAGES } from "../data/siteData";

const BENEFITS = [
  "Free remote estimate using satellite imagery — no in-person pressure",
  "Instant ballpark price above — confirmed in writing after measurement",
  "Flat-rate pricing, zero surprises",
  "One-day install scheduled around your life",
  "Lifetime limited warranty explained line by line",
];

const TRUST = [
  { icon: ShieldCheck, label: "Licensed & Insured" },
  { icon: Clock, label: "1-Day Install" },
  { icon: Award, label: "Lifetime Warranty" },
  { icon: BadgeDollarSign, label: "Flat-Rate Pricing" },
];

export default function FreeEstimatePage() {
  const [prefill, setPrefill] = useState(null);
  const formRef = useRef(null);

  const handleLockIn = (data) => {
    setPrefill({
      garage_size: data.garage_size,
      message: data.message,
      // Best-effort city inference is left to sales; we still capture ZIP in message.
    });
    // Scroll to lead form
    setTimeout(() => {
      formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 60);
  };

  return (
    <>
      <SEO
        title="Free Garage Floor Quote Phoenix | Instant Estimate | Vantyx Coatings"
        description="Get an instant ballpark price for your garage floor coating in 30 seconds — plus a free confirmed quote via satellite imagery. One-day install, lifetime warranty. Phoenix Metro."
        path="/free-estimate"
        ogImage={IMAGES.garage1}
        schemas={[localBusinessSchema()]}
      />
      {/* Stripped header — logo only */}
      <header className="bg-[#000000] border-b border-white/10" data-testid="estimate-stripped-header">
        <div className="max-w-6xl mx-auto px-6 h-20 flex items-center justify-between">
          <Logo variant="light" className="h-12" />
          <a href={BRAND.phoneHref} data-testid="estimate-header-phone" className="flex items-center gap-2 text-white font-semibold text-sm hover:text-[#028090] transition-colors">
            <Phone size={16} className="text-[#028090]" strokeWidth={1.5} /> {BRAND.phone}
          </a>
        </div>
      </header>

      <main className="hero-gradient grain relative min-h-screen">
        <div className="max-w-6xl mx-auto px-6 md:px-12 py-16 md:py-24 grid lg:grid-cols-2 gap-12 lg:gap-16 items-start">
          <div>
            <SectionLabel>Free · Instant · No Obligation</SectionLabel>
            <h1 className="font-display text-4xl sm:text-5xl font-extrabold tracking-tight text-white leading-[1.08]">
              Get Your <span className="text-[#028090]">Instant Ballpark</span>, Then Your Free Confirmed Quote
            </h1>
            <p className="text-gray-300 text-base md:text-lg mt-6">Answer three quick questions and see your price range right now. Prefer to skip ahead? Fill the form and we'll come back within one business day.</p>
            <ul className="mt-6 space-y-4">
              {BENEFITS.map((b) => (
                <li key={b} className="flex items-start gap-3 text-gray-200">
                  <Check size={20} className="text-[#028090] mt-0.5 shrink-0" strokeWidth={2} /> {b}
                </li>
              ))}
            </ul>
            <div className="flex items-center gap-3 mt-8">
              <Stars />
              <span className="text-gray-400 text-sm">5.0 rated across the Phoenix Metro</span>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-10" data-testid="estimate-trust-icons">
              {TRUST.map((t) => (
                <div key={t.label} className="flex flex-col items-center gap-2 text-center border border-white/10 bg-white/5 p-4">
                  <t.icon size={22} className="text-[#028090]" strokeWidth={1.5} />
                  <span className="text-gray-300 text-xs font-semibold">{t.label}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="lg:sticky lg:top-8 space-y-6">
            <InstantEstimator onLockInPrice={handleLockIn} />
            <div ref={formRef} className="bg-[#0a0a0a] border border-white/10 p-8 md:p-10 teal-glow scroll-mt-24" data-testid="lead-form-block">
              <h2 className="font-display text-2xl font-bold text-white mb-2">
                {prefill ? "Lock in your price →" : "Or skip ahead — full quote below"}
              </h2>
              <p className="text-gray-400 text-sm mb-7">
                {prefill ? "Your instant estimate is attached. We'll confirm exact pricing via free satellite measurement within one business day." : "Takes 30 seconds. We'll reach out within one business day with your flat-rate quote."}
              </p>
              <LeadForm dark sourceTag="free-estimate-lp" prefill={prefill} />
            </div>
          </div>
        </div>
        <footer className="border-t border-white/10 py-8 text-center">
          <p className="text-gray-500 text-xs">© {new Date().getFullYear()} Vantyx Coatings LLC · {BRAND.phone} · {BRAND.email} · Phoenix Metro Area, AZ</p>
        </footer>
      </main>
    </>
  );
}
